import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  FileEdit, CheckCircle2, XCircle, Clock, Eye, ArrowLeftRight,
  Building2, Settings, RefreshCw, Loader2, AlertTriangle, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Pendente': 'bg-amber-100 text-amber-800 border-amber-300',
  'Aprovado': 'bg-green-100 text-green-800 border-green-300',
  'Rejeitado': 'bg-red-100 text-red-800 border-red-300',
  'Auto-Aprovado': 'bg-blue-100 text-blue-800 border-blue-300'
};

const FIELD_LABELS = {
  'nome': 'Nome',
  'nif': 'NIF',
  'tipo_entidade': 'Tipo de Entidade',
  'distrito_id': 'Distrito',
  'concelho_id': 'Concelho',
  'morada': 'Morada',
  'codigo_postal': 'Código Postal',
  'telefone': 'Telefone',
  'telemovel': 'Telemóvel',
  'email_principal': 'Email Principal',
  'email_secundario': 'Email Secundário',
  'pessoa_contacto': 'Pessoa de Contacto',
  'contacto_cargo': 'Cargo do Contacto',
  'contacto_telefone': 'Telefone do Contacto',
  'contacto_email': 'Email do Contacto',
  'presidente_nome': 'Nome do Presidente',
  'presidente_cargo': 'Cargo do Presidente',
  'presidente_telefone': 'Telefone do Presidente',
  'presidente_email': 'Email do Presidente',
  'website': 'Website',
  'fax': 'Fax'
};

export default function PedidosAlteracaoPage() {
  const [pedidos, setPedidos] = useState([]);
  const [stats, setStats] = useState({ pendentes: 0, aprovados: 0, rejeitados: 0, auto_aprovados: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filterStatus, setFilterStatus] = useState('Pendente');
  const [config, setConfig] = useState(null);

  const loadPedidos = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [pedidosRes, statsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pedidos-alteracao?status=${filterStatus}`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pedidos-alteracao/stats`, { headers })
      ]);

      if (pedidosRes.ok) {
        const data = await pedidosRes.json();
        setPedidos(data.pedidos || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error loading pedidos:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pedidos-alteracao/configuracao/aprovacao`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  useEffect(() => {
    loadPedidos();
    loadConfig();
  }, [loadPedidos]);

  const handleAprovar = async () => {
    if (!selectedPedido) return;
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pedidos-alteracao/${selectedPedido.id}/aprovar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Alterações aprovadas com sucesso!');
        setShowDetailDialog(false);
        setSelectedPedido(null);
        loadPedidos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao aprovar');
      }
    } catch (error) {
      toast.error('Erro ao aprovar alterações');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejeitar = async () => {
    if (!selectedPedido || !rejectReason.trim()) {
      toast.error('Indique o motivo da rejeição');
      return;
    }
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pedidos-alteracao/${selectedPedido.id}/rejeitar?motivo=${encodeURIComponent(rejectReason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Pedido rejeitado');
        setShowRejectDialog(false);
        setShowDetailDialog(false);
        setSelectedPedido(null);
        setRejectReason('');
        loadPedidos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao rejeitar');
      }
    } catch (error) {
      toast.error('Erro ao rejeitar pedido');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!config) return;
    setProcessing(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pedidos-alteracao/configuracao/aprovacao`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        toast.success('Configuração guardada!');
        setShowConfigDialog(false);
      } else {
        toast.error('Erro ao guardar configuração');
      }
    } catch (error) {
      toast.error('Erro ao guardar');
    } finally {
      setProcessing(false);
    }
  };

  const openDetail = (pedido) => {
    setSelectedPedido(pedido);
    setShowDetailDialog(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFieldLabel = (field) => FIELD_LABELS[field] || field;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pedidos-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pedidos-alteracao-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileEdit className="w-6 h-6 text-[#0066CC]" />
            Validação de Alterações de Dados
          </h1>
          <p className="text-slate-500">Aprovar ou rejeitar alterações submetidas pelas instituições</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadPedidos}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button variant="outline" onClick={() => setShowConfigDialog(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Configurações
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`cursor-pointer transition-all ${filterStatus === 'Pendente' ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => setFilterStatus('Pendente')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.pendentes}</p>
                <p className="text-xs text-slate-500">Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${filterStatus === 'Aprovado' ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setFilterStatus('Aprovado')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.aprovados}</p>
                <p className="text-xs text-slate-500">Aprovados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${filterStatus === 'Rejeitado' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => setFilterStatus('Rejeitado')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.rejeitados}</p>
                <p className="text-xs text-slate-500">Rejeitados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer transition-all ${filterStatus === 'Auto-Aprovado' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setFilterStatus('Auto-Aprovado')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.auto_aprovados}</p>
                <p className="text-xs text-slate-500">Auto-Aprovados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Alert */}
      {stats.pendentes > 0 && filterStatus !== 'Pendente' && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <p className="text-amber-800">
                Tem <strong>{stats.pendentes}</strong> pedido(s) pendente(s) de aprovação.{' '}
                <button 
                  className="underline font-medium"
                  onClick={() => setFilterStatus('Pendente')}
                >
                  Ver pedidos pendentes
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Requests List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Pedidos - {filterStatus}
            </CardTitle>
            <Badge variant="outline">{pedidos.length} pedido(s)</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {pedidos.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileEdit className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>Nenhum pedido {filterStatus.toLowerCase()} encontrado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pedidos.map((pedido) => (
                <div 
                  key={pedido.id}
                  className="border rounded-lg p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => openDetail(pedido)}
                  data-testid={`pedido-${pedido.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{pedido.instituicao_nome}</span>
                        {pedido.instituicao_codigo_ea && (
                          <Badge variant="outline" className="text-xs">{pedido.instituicao_codigo_ea}</Badge>
                        )}
                      </div>
                      <div className="text-sm text-slate-500 mb-2">
                        <span>Submetido por: {pedido.submetido_por_nome}</span>
                        <span className="mx-2">•</span>
                        <span>{formatDate(pedido.created_at)}</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(pedido.campos_alterados || []).map((campo) => (
                          <Badge key={campo} variant="secondary" className="text-xs">
                            {getFieldLabel(campo)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={STATUS_COLORS[pedido.status] || 'bg-slate-100'}>
                        {pedido.status}
                      </Badge>
                      <Button size="sm" variant="ghost">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-[#0066CC]" />
              Comparação de Alterações
            </DialogTitle>
            <DialogDescription>
              {selectedPedido?.instituicao_nome} - {formatDate(selectedPedido?.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPedido && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <span>Submetido por:</span>
                <strong>{selectedPedido.submetido_por_nome}</strong>
              </div>

              {/* Changes Comparison */}
              <div className="border rounded-lg overflow-hidden">
                <div className="grid grid-cols-3 bg-slate-100 text-sm font-medium">
                  <div className="p-3 border-r">Campo</div>
                  <div className="p-3 border-r text-red-700">Valor Anterior</div>
                  <div className="p-3 text-green-700">Novo Valor</div>
                </div>
                {Object.entries(selectedPedido.dados_novos || {}).map(([campo, valorNovo]) => (
                  <div key={campo} className="grid grid-cols-3 border-t text-sm">
                    <div className="p-3 border-r font-medium bg-slate-50">
                      {getFieldLabel(campo)}
                    </div>
                    <div className="p-3 border-r text-red-600 bg-red-50/50">
                      {selectedPedido.dados_antigos?.[campo] || <span className="text-slate-400 italic">vazio</span>}
                    </div>
                    <div className="p-3 text-green-700 bg-green-50/50 font-medium">
                      {valorNovo || <span className="text-slate-400 italic">vazio</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Status Info */}
              {selectedPedido.status !== 'Pendente' && (
                <Card className="bg-slate-50">
                  <CardContent className="pt-4">
                    <div className="text-sm space-y-1">
                      <p><strong>Estado:</strong> {selectedPedido.status}</p>
                      {selectedPedido.aprovado_por_nome && (
                        <p><strong>Aprovado por:</strong> {selectedPedido.aprovado_por_nome} em {formatDate(selectedPedido.data_aprovacao)}</p>
                      )}
                      {selectedPedido.rejeitado_por_nome && (
                        <>
                          <p><strong>Rejeitado por:</strong> {selectedPedido.rejeitado_por_nome} em {formatDate(selectedPedido.data_rejeicao)}</p>
                          <p><strong>Motivo:</strong> {selectedPedido.motivo_rejeicao}</p>
                        </>
                      )}
                      {selectedPedido.motivo_auto_aprovacao && (
                        <p><strong>Motivo:</strong> {selectedPedido.motivo_auto_aprovacao}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedPedido?.status === 'Pendente' && (
              <>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowRejectDialog(true)}
                  disabled={processing}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={handleAprovar}
                  disabled={processing}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Aprovar Alterações
                </Button>
              </>
            )}
            {selectedPedido?.status !== 'Pendente' && (
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                Fechar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Rejeitar Pedido
            </DialogTitle>
            <DialogDescription>
              Indique o motivo da rejeição. A instituição será notificada.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Motivo da Rejeição *</Label>
              <Textarea 
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Dados inconsistentes, informação em falta..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRejeitar}
              disabled={processing || !rejectReason.trim()}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#0066CC]" />
              Configurações de Aprovação
            </DialogTitle>
            <DialogDescription>
              Defina quais campos são aprovados automaticamente
            </DialogDescription>
          </DialogHeader>
          
          {config && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="config-ativo"
                  checked={config.ativo}
                  onChange={(e) => setConfig({...config, ativo: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="config-ativo">Sistema de validação ativo</Label>
              </div>

              <div className="space-y-2">
                <Label className="text-green-700">Campos com Aprovação Automática</Label>
                <p className="text-xs text-slate-500">Alterações nestes campos são aplicadas imediatamente</p>
                <div className="flex flex-wrap gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  {(config.campos_auto_aprovacao || []).map((campo) => (
                    <Badge key={campo} className="bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer"
                           onClick={() => {
                             setConfig({
                               ...config,
                               campos_auto_aprovacao: config.campos_auto_aprovacao.filter(c => c !== campo)
                             });
                           }}>
                      {getFieldLabel(campo)} ×
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-amber-700">Campos que Requerem Aprovação</Label>
                <p className="text-xs text-slate-500">Alterações nestes campos precisam de validação manual</p>
                <div className="flex flex-wrap gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  {(config.campos_requerem_aprovacao || []).map((campo) => (
                    <Badge key={campo} className="bg-amber-100 text-amber-800">
                      {getFieldLabel(campo)}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={processing}>
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Guardar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
